import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignatureIcon, PencilIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  DatePicker,
  EmptyState,
  ErrorNotice,
  FactList,
  Field,
  LoadingState,
  Panel,
  Pill,
  SearchInput,
  SelectInput,
  TextArea,
  TextInput,
  type DataTableSort,
  type PillTone,
} from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  REQUEST_KIND_LABELS,
  REQUEST_STATUS_LABELS,
  approveAgreementRequest,
  decideCancellation,
  declineAgreementRequest,
  readOrganizationAgreement,
  saveOrganizationAgreement,
  type Agreement,
  type AgreementRequest,
  type AgreementWrite,
} from '@/lib/agreements';
import { ContractsTab } from '@/pages/event-contracts-tab';

const usd = (value: number): string => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const day = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—';
/** A moment something happened, in the reader's own time. */
const when = (value: string): string => new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const STATUS_TONE: Readonly<Record<AgreementRequest['status'], PillTone>> = {
  REQUESTED: 'warning',
  APPROVED: 'success',
  DRAFTING: 'info',
  IN_REVIEW: 'info',
  READY_TO_SIGN: 'brand',
  SIGNED: 'success',
  PAID: 'success',
  DECLINED: 'neutral',
  CANCEL_REQUESTED: 'warning',
  CANCELED: 'neutral',
};

/** The newest credits change on a request, ignoring any written before the before-figure existed. */
function latestChange(request: AgreementRequest): { readonly at: string; readonly from: number; readonly credits: number } | null {
  const changes = (request.changeRequests ?? []).filter((change) => typeof change.from === 'number');
  return changes[changes.length - 1] ?? null;
}

const OPEN_STATUSES: readonly AgreementRequest['status'][] = ['REQUESTED', 'APPROVED', 'DRAFTING', 'IN_REVIEW', 'READY_TO_SIGN', 'CANCEL_REQUESTED'];

const SORT_FIELDS = { kind: 'kind', credits: 'credits', total: 'total', termStart: 'termStart', status: 'status', createdAt: 'createdAt' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.createdAt, direction: 'desc' };

/** A request with its kind and status as words, for the search. */
interface RequestSearchRow extends AgreementRequest {
  readonly kindLabel: string;
  readonly statusLabel: string;
}

/** The row fields the search box matches. */
const REQUEST_SEARCH_FIELDS: ReadonlyArray<keyof RequestSearchRow> = ['kindLabel', 'statusLabel', 'requestedByName', 'note'];

/** Its own URL key: the organization page has other searchable lists. */
const REQUEST_SEARCH_KEY = 'requestsQ';

/**
 * The Agreement tab of an organization (§46): the agreement of record, the
 * client's package requests, and the contract that answers the open one.
 *
 * The client asks from their portal and signs there; BCE edits the terms
 * here and drafts the contract with the same tooling an event uses. When the
 * contract is signed the agreement and the credits are written by the
 * signature webhook, not by anything on this tab.
 *
 * @param props - The organization.
 * @returns The tab.
 */
export function OrgAgreementTab({
  organizationId,
  organizationName,
}: {
  readonly organizationId: string;
  readonly organizationName: string;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [declining, setDeclining] = useState<AgreementRequest | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch(REQUEST_SEARCH_KEY);

  const view = useQuery({
    queryKey: ['org-agreement', organizationId],
    queryFn: ({ signal }) => readOrganizationAgreement(organizationId, signal),
  });

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['org-agreement', organizationId] });
    await queryClient.invalidateQueries({ queryKey: ['org-credits', organizationId] });
  }
  const approve = useMutation({
    mutationFn: (requestId: string) => approveAgreementRequest(organizationId, requestId),
    onSuccess: async () => {
      toast.success('Request approved — the client has been told');
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const cancellation = useMutation({
    mutationFn: ({ requestId, approve: yes }: { requestId: string; approve: boolean }) => decideCancellation(organizationId, requestId, yes),
    onSuccess: async (_result, { approve: yes }) => {
      toast.success(yes ? 'Cancellation approved — the client has been told' : 'Request kept — the client has been told');
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (view.isPending) return <LoadingState label="Loading the agreement…" />;
  if (view.isError) return <ErrorNotice title="Agreement unavailable" message={errorMessage(view.error)} />;

  const { agreement, requests, history } = view.data;
  const open = requests.find((request) => OPEN_STATUSES.includes(request.status)) ?? null;
  const selected = requests.find((request) => request._id === (selectedId ?? open?._id)) ?? open;
  const rows = sortRows(
    filterRows<RequestSearchRow>(
      requests.map((request) => ({ ...request, kindLabel: REQUEST_KIND_LABELS[request.kind], statusLabel: REQUEST_STATUS_LABELS[request.status] })),
      search,
      REQUEST_SEARCH_FIELDS,
    ),
    sort,
  );

  return (
    <div className="mt-6 space-y-6">
      <section>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-portal-section">Agreement of record</h2>
            <p className="text-portal-micro text-portal-muted">
              {organizationName}&rsquo;s current term, credits and preferred pricing. The client reads this on their Agreement page and asks for changes through a request.
            </p>
          </div>
          <Button variant="secondary" onClick={() => setEditing(true)}>
            <PencilIcon aria-hidden className="size-4" /> {agreement ? 'Edit terms' : 'Set up the agreement'}
          </Button>
        </div>

        {agreement ? (
          <Panel className="p-5">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Pill tone={agreement.status === 'ACTIVE' ? 'success' : 'neutral'}>{agreement.status.toLowerCase()}</Pill>
              <span className="text-portal-small text-portal-muted">
                {day(agreement.termStart)} to {day(agreement.termEnd)}
              </span>
            </div>
            <FactList
              columns={4}
              facts={[
                { label: 'Credits purchased', value: agreement.creditsPurchased.toLocaleString('en-US') },
                { label: 'List price per credit', value: agreement.unitPrice ? usd(agreement.unitPrice) : '—' },
                { label: 'Preferred-pricing discount', value: `${agreement.discountPercent ?? 0}%` },
                { label: 'Expiry policy', value: agreement.expirationPolicy.split('_').join(' ').toLowerCase() },
                { label: 'Carry-forward cap', value: agreement.carryForwardCap ? `${agreement.carryForwardCap} credits` : 'None' },
                { label: 'Additional credits', value: agreement.additionalCreditPrice ? `${usd(agreement.additionalCreditPrice)} each` : '—' },
                { label: 'Renewal', value: agreement.autoRenew === false ? 'Does not renew on its own' : `Renews on its own at ${agreement.creditsPurchased.toLocaleString('en-US')} credits` },
                { label: 'Purchase order', value: agreement.purchaseOrderRequired ? `Required${agreement.purchaseOrderNumber ? ` · ${agreement.purchaseOrderNumber}` : ''}` : 'Not required' },
              ]}
            />
            {history.length > 0 ? (
              <p className="mt-4 border-t border-portal-line pt-3 text-portal-micro text-portal-muted">
                {history.length} earlier {history.length === 1 ? 'agreement' : 'agreements'}: {history.map((row) => `${day(row.termStart)}–${day(row.termEnd)} (${row.creditsPurchased.toLocaleString('en-US')} credits)`).join(' · ')}
              </p>
            ) : null}
          </Panel>
        ) : (
          <Panel>
            <EmptyState
              icon={<FileSignatureIcon aria-hidden className="size-6" />}
              title="No agreement on file"
              message="Set the term, credits and discount here, or wait for the client to ask for credits from their portal and answer it with a contract — signing creates the agreement and applies the credits."
              action={<Button onClick={() => setEditing(true)}>Set up the agreement</Button>}
            />
          </Panel>
        )}
      </section>

      <section>
        <div className="mb-3">
          <h2 className="text-portal-section">Credit requests</h2>
          <p className="text-portal-micro text-portal-muted">
            What the client asked for from their portal, at the price they saw. Answer the open one with a contract below; decline it with a reason if it is not going ahead.
          </p>
        </div>
        {requests.length > 0 ? (
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <SearchInput
              label="Search credit requests"
              placeholder="Request, status, requester or note"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
          </div>
        ) : null}
        {requests.length === 0 ? (
          <Panel>
            <EmptyState
              title="No requests yet"
              message="When an admin asks for a first agreement, a different renewal or more credits on their portal, it appears here for BCE to draft the agreement."
            />
          </Panel>
        ) : rows.length === 0 ? (
          <Panel>
            <EmptyState title="No requests match" message="Try a different search." />
          </Panel>
        ) : (
          <DataTable
            headers={[
              { label: 'Request', sortKey: SORT_FIELDS.kind },
              { label: 'Credits', sortKey: SORT_FIELDS.credits },
              { label: 'Price', sortKey: SORT_FIELDS.total },
              { label: 'Term', sortKey: SORT_FIELDS.termStart },
              { label: 'Status', sortKey: SORT_FIELDS.status },
              'Actions',
            ]}
            rightAlignedHeaders={[1, 2]}
            visuallyHiddenHeaders={[5]}
            sort={sort}
            onSort={onSort}
            minWidth={860}
          >
            {rows.map((request) => (
              <tr key={request._id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <span className="block font-bold">{REQUEST_KIND_LABELS[request.kind]}</span>
                  <span className="block text-portal-micro text-portal-muted">
                    {when(request.createdAt)}{request.requestedByName ? ` · ${request.requestedByName}` : ''}
                  </span>
                  {request.note ? <span className="block max-w-[360px] truncate text-portal-micro text-portal-muted">“{request.note}”</span> : null}
                  {latestChange(request) ? (
                    <span className="block text-portal-micro font-bold text-portal-ink">
                      Changed {when(latestChange(request)?.at ?? request.createdAt)}: {latestChange(request)?.from.toLocaleString('en-US')} → {request.credits.toLocaleString('en-US')} credits
                    </span>
                  ) : null}
                </td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap">{request.credits.toLocaleString('en-US')}</td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap">
                  <span className="block font-bold">{usd(request.total)}</span>
                  <span className="block text-portal-micro text-portal-muted">
                    {usd(request.unitPrice)} each{request.discountPercent > 0 ? ` · ${request.discountPercent}% off` : ''}
                  </span>
                </td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">
                  {request.termStart ? `${day(request.termStart)} – ${day(request.termEnd)}` : '—'}
                </td>
                <td className="p-3 text-portal-small">
                  <Pill tone={STATUS_TONE[request.status]}>{REQUEST_STATUS_LABELS[request.status]}</Pill>
                  {request.status === 'REQUESTED' && request.requiresApproval ? <span className="mt-1 block text-portal-micro text-portal-muted">Lowers the renewal · needs your approval</span> : null}
                  {request.status === 'CANCEL_REQUESTED' ? <span className="mt-1 block text-portal-micro text-portal-muted">Inside the 90-day notice · your call</span> : null}
                  {request.paidAt ? <span className="mt-1 block text-portal-micro text-portal-muted">Paid {when(request.paidAt)}{request.amountPaid ? ` · ${usd(request.amountPaid)}` : ''}</span> : null}
                  {request.declinedReason ? <span className="mt-1 block max-w-[240px] truncate text-portal-micro text-portal-muted">{request.declinedReason}</span> : null}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <span className="inline-flex gap-2">
                    {selected?._id !== request._id ? (
                      <Button size="compact" variant="secondary" onClick={() => setSelectedId(request._id)}>
                        Contracts
                      </Button>
                    ) : null}
                    {request.status === 'REQUESTED' && request.requiresApproval ? (
                      <Button size="compact" variant="primary" disabled={approve.isPending} onClick={() => approve.mutate(request._id)}>
                        Approve
                      </Button>
                    ) : null}
                    {request.status === 'CANCEL_REQUESTED' ? (
                      <>
                        <Button size="compact" variant="primary" disabled={cancellation.isPending} onClick={() => cancellation.mutate({ requestId: request._id, approve: true })}>
                          Approve cancellation
                        </Button>
                        <Button size="compact" variant="secondary" disabled={cancellation.isPending} onClick={() => cancellation.mutate({ requestId: request._id, approve: false })}>
                          Keep
                        </Button>
                      </>
                    ) : null}
                    {request.status === 'REQUESTED' || request.status === 'APPROVED' || request.status === 'DRAFTING' ? (
                      <Button size="compact" variant="secondary" onClick={() => setDeclining(request)}>
                        Decline
                      </Button>
                    ) : null}
                  </span>
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </section>

      {selected ? (
        <section>
          <div className="mb-3">
            <h2 className="text-portal-section">
              Agreement for the {REQUEST_KIND_LABELS[selected.kind].toLowerCase()} request of {day(selected.createdAt)}
            </h2>
            <p className="text-portal-micro text-portal-muted">
              Generate the enterprise agreement from the request, share it for review, then publish it for signature. The client signs on their Agreement page; the credits enter their pool when they do.
            </p>
          </div>
          <ContractsTab subject={{ kind: 'agreement', id: selected._id, organizationId }} />
        </section>
      ) : null}

      {editing ? (
        <AgreementDialog
          organizationId={organizationId}
          agreement={agreement}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await refresh();
          }}
        />
      ) : null}
      {declining ? (
        <DeclineDialog
          organizationId={organizationId}
          request={declining}
          onClose={() => setDeclining(null)}
          onDeclined={async () => {
            setDeclining(null);
            await refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/** Set up or edit the agreement of record. */
function AgreementDialog({
  organizationId,
  agreement,
  onClose,
  onSaved,
}: {
  readonly organizationId: string;
  readonly agreement: Agreement | null;
  readonly onClose: () => void;
  readonly onSaved: () => Promise<void>;
}): React.ReactElement {
  const [status, setStatus] = useState<Agreement['status']>(agreement?.status ?? 'ACTIVE');
  const [termStart, setTermStart] = useState(agreement?.termStart.slice(0, 10) ?? '');
  const [termEnd, setTermEnd] = useState(agreement?.termEnd.slice(0, 10) ?? '');
  const [creditsPurchased, setCreditsPurchased] = useState(agreement ? String(agreement.creditsPurchased) : '');
  const [unitPrice, setUnitPrice] = useState(agreement?.unitPrice ? String(agreement.unitPrice) : '');
  const [discountPercent, setDiscountPercent] = useState(String(agreement?.discountPercent ?? 0));
  const [expirationPolicy, setExpirationPolicy] = useState<Agreement['expirationPolicy']>(agreement?.expirationPolicy ?? 'EXPIRE');
  const [carryForwardCap, setCarryForwardCap] = useState(agreement?.carryForwardCap ? String(agreement.carryForwardCap) : '');
  const [additionalCreditPrice, setAdditionalCreditPrice] = useState(agreement?.additionalCreditPrice ? String(agreement.additionalCreditPrice) : '');
  const [renewalTerms, setRenewalTerms] = useState(agreement?.renewalTerms ?? '');
  const [cancellationTerms, setCancellationTerms] = useState(agreement?.cancellationTerms ?? '');
  const [billingOffice, setBillingOffice] = useState(agreement?.billingOffice ?? '');
  const [accountsPayableName, setAccountsPayableName] = useState(agreement?.accountsPayableName ?? '');
  const [accountsPayableEmail, setAccountsPayableEmail] = useState(agreement?.accountsPayableEmail ?? '');
  const [purchaseOrderRequired, setPurchaseOrderRequired] = useState(agreement?.purchaseOrderRequired ?? false);
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState(agreement?.purchaseOrderNumber ?? '');
  const [taxExempt, setTaxExempt] = useState(agreement?.taxExempt ?? false);
  const [autoRenew, setAutoRenew] = useState(agreement?.autoRenew ?? true);
  const [bceFinancialContact, setBceFinancialContact] = useState(agreement?.bceFinancialContact ?? '');
  const [clientExecSponsor, setClientExecSponsor] = useState(agreement?.clientExecSponsor ?? '');
  const [clientContractAdmin, setClientContractAdmin] = useState(agreement?.clientContractAdmin ?? '');
  const [notes, setNotes] = useState(agreement?.notes ?? '');

  const nullable = (value: string): string | null => (value.trim() ? value.trim() : null);
  const numberOrNull = (value: string): number | null => (value.trim() ? Number(value) : null);

  const save = useMutation({
    mutationFn: () => {
      const body: AgreementWrite = {
        status,
        ...(termStart ? { termStart: new Date(`${termStart}T00:00:00Z`).toISOString() } : {}),
        ...(termEnd ? { termEnd: new Date(`${termEnd}T00:00:00Z`).toISOString() } : {}),
        ...(creditsPurchased.trim() ? { creditsPurchased: Number(creditsPurchased) } : {}),
        unitPrice: numberOrNull(unitPrice),
        discountPercent: Number(discountPercent) || 0,
        expirationPolicy,
        carryForwardCap: numberOrNull(carryForwardCap),
        additionalCreditPrice: numberOrNull(additionalCreditPrice),
        autoRenew,
        renewalTerms: nullable(renewalTerms),
        cancellationTerms: nullable(cancellationTerms),
        billingOffice: nullable(billingOffice),
        accountsPayableName: nullable(accountsPayableName),
        accountsPayableEmail: nullable(accountsPayableEmail),
        purchaseOrderRequired,
        purchaseOrderNumber: nullable(purchaseOrderNumber),
        taxExempt,
        bceFinancialContact: nullable(bceFinancialContact),
        clientExecSponsor: nullable(clientExecSponsor),
        clientContractAdmin: nullable(clientContractAdmin),
        notes: nullable(notes),
      };
      return saveOrganizationAgreement(organizationId, body);
    },
    onSuccess: async () => {
      toast.success('Agreement saved');
      await onSaved();
    },
  });

  const valid = agreement !== null || (termStart !== '' && termEnd !== '');

  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogTitle>{agreement ? 'Edit the agreement' : 'Set up the agreement'}</DialogTitle>
        <form
          className="mt-2 space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !save.isPending) save.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Status" htmlFor="agreement-status">
              <SelectInput id="agreement-status" value={status} onChange={(event) => setStatus(event.target.value as Agreement['status'])}>
                <option value="DRAFT">Draft</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRED">Expired</option>
                <option value="CANCELLED">Canceled</option>
              </SelectInput>
            </Field>
            <Field label="Term start" htmlFor="agreement-term-start" required={agreement === null}>
              <DatePicker id="agreement-term-start" value={termStart} onChange={setTermStart} />
            </Field>
            <Field label="Term end" htmlFor="agreement-term-end" required={agreement === null}>
              <DatePicker id="agreement-term-end" value={termEnd} onChange={setTermEnd} />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Credits purchased" htmlFor="agreement-credits" hint="The figure on the agreement; the ledger is applied separately on Credits">
              <TextInput id="agreement-credits" type="number" min={0} step={1} value={creditsPurchased} onChange={(event) => setCreditsPurchased(event.target.value)} />
            </Field>
            <Field label="List price per credit" htmlFor="agreement-unit-price">
              <TextInput id="agreement-unit-price" type="number" min={0} step="0.01" value={unitPrice} onChange={(event) => setUnitPrice(event.target.value)} />
            </Field>
            <Field label="Preferred-pricing discount (%)" htmlFor="agreement-discount" hint="Applied to every credit this client buys">
              <TextInput id="agreement-discount" type="number" min={0} max={100} step="0.5" value={discountPercent} onChange={(event) => setDiscountPercent(event.target.value)} />
            </Field>
            <Field label="Expiry policy" htmlFor="agreement-expiry">
              <SelectInput id="agreement-expiry" value={expirationPolicy} onChange={(event) => setExpirationPolicy(event.target.value as Agreement['expirationPolicy'])}>
                <option value="EXPIRE">Expire at term end</option>
                <option value="CARRY_FORWARD">Carry forward</option>
                <option value="CARRY_FORWARD_CAPPED">Carry forward, capped</option>
              </SelectInput>
            </Field>
            <Field label="Carry-forward cap" htmlFor="agreement-cap">
              <TextInput id="agreement-cap" type="number" min={0} step={1} value={carryForwardCap} onChange={(event) => setCarryForwardCap(event.target.value)} />
            </Field>
            <Field label="Additional credit price" htmlFor="agreement-addl">
              <TextInput id="agreement-addl" type="number" min={0} step="0.01" value={additionalCreditPrice} onChange={(event) => setAdditionalCreditPrice(event.target.value)} />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-portal-small">
            <Checkbox checked={autoRenew} onCheckedChange={(next) => setAutoRenew(next === true)} /> Renews on its own at the same credits when the term ends
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Renewal terms" htmlFor="agreement-renewal">
              <TextArea id="agreement-renewal" rows={2} value={renewalTerms} onChange={(event) => setRenewalTerms(event.target.value)} />
            </Field>
            <Field label="Cancellation terms" htmlFor="agreement-cancellation">
              <TextArea id="agreement-cancellation" rows={2} value={cancellationTerms} onChange={(event) => setCancellationTerms(event.target.value)} />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Billing office" htmlFor="agreement-billing">
              <TextInput id="agreement-billing" value={billingOffice} onChange={(event) => setBillingOffice(event.target.value)} />
            </Field>
            <Field label="Accounts payable" htmlFor="agreement-ap-name">
              <TextInput id="agreement-ap-name" value={accountsPayableName} onChange={(event) => setAccountsPayableName(event.target.value)} />
            </Field>
            <Field label="AP email" htmlFor="agreement-ap-email">
              <TextInput id="agreement-ap-email" type="email" value={accountsPayableEmail} onChange={(event) => setAccountsPayableEmail(event.target.value)} />
            </Field>
            <Field label="Purchase order number" htmlFor="agreement-po">
              <TextInput id="agreement-po" value={purchaseOrderNumber} onChange={(event) => setPurchaseOrderNumber(event.target.value)} />
            </Field>
            <label className="flex items-center gap-2 self-end pb-2 text-portal-small">
              <Checkbox checked={purchaseOrderRequired} onCheckedChange={(next) => setPurchaseOrderRequired(next === true)} /> Purchase order required
            </label>
            <label className="flex items-center gap-2 self-end pb-2 text-portal-small">
              <Checkbox checked={taxExempt} onCheckedChange={(next) => setTaxExempt(next === true)} /> Tax exempt
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="BCE financial contact" htmlFor="agreement-bce-contact">
              <TextInput id="agreement-bce-contact" value={bceFinancialContact} onChange={(event) => setBceFinancialContact(event.target.value)} />
            </Field>
            <Field label="Client executive sponsor" htmlFor="agreement-sponsor">
              <TextInput id="agreement-sponsor" value={clientExecSponsor} onChange={(event) => setClientExecSponsor(event.target.value)} />
            </Field>
            <Field label="Client contract administrator" htmlFor="agreement-admin">
              <TextInput id="agreement-admin" value={clientContractAdmin} onChange={(event) => setClientContractAdmin(event.target.value)} />
            </Field>
          </div>

          <Field label="Notes" htmlFor="agreement-notes" hint="Shown to the client on their Agreement page">
            <TextArea id="agreement-notes" rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Field>

          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save agreement'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Turn a request down with a reason the client reads. */
function DeclineDialog({
  organizationId,
  request,
  onClose,
  onDeclined,
}: {
  readonly organizationId: string;
  readonly request: AgreementRequest;
  readonly onClose: () => void;
  readonly onDeclined: () => Promise<void>;
}): React.ReactElement {
  const [reason, setReason] = useState('');
  const decline = useMutation({
    mutationFn: () => declineAgreementRequest(organizationId, request._id, reason.trim()),
    onSuccess: async () => {
      toast.success('Request declined');
      await onDeclined();
    },
  });
  return (
    <Dialog open onOpenChange={(next) => (!next && !decline.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Decline this request?</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (reason.trim() && !decline.isPending) decline.mutate();
          }}
        >
          <p className="text-portal-small text-portal-muted">
            {REQUEST_KIND_LABELS[request.kind]} · {request.credits.toLocaleString('en-US')} credits. The client sees the reason on their Agreement page and can ask again.
          </p>
          <Field label="Reason" htmlFor="decline-reason" required>
            <TextArea id="decline-reason" rows={3} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>
          {decline.isError ? <ErrorNotice inline title="Not declined" message={errorMessage(decline.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={decline.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!reason.trim() || decline.isPending}>
              {decline.isPending ? 'Declining…' : 'Decline request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
